import api from "../../../Utils/interface";
import { getBulkUploadConfig } from "./Json/BulkUploadConfigJson";
import { BULK_UPLOAD_MESSAGES } from "./Json/BulkUploadMessages";
import {
  buildResultFilename,
  detectResponseKind,
  filenameFromDisposition,
  parseTextBody,
  pickMessage,
  readBlobAsText,
  RESPONSE_KIND,
} from "./Utils/responseUtils";

const MULTIPART_HEADERS = { "Content-Type": "multipart/form-data" };
const FORM_FIELD_NAME = "file";

/**
 * The outcome of one upload.
 *
 * @typedef  {object} BulkUploadResult
 * @property {{ blob: Blob, filename: string }|null} file  Result file to download.
 * @property {string|null} message  Shown when the response carried no file.
 */

/** @returns {BulkUploadResult} */
const toResult = (file = null, message = null) => ({ file, message });

/**
 * Turns an axios response into a BulkUploadResult.
 *
 * A file response is handed straight through for download - the per-row
 * outcome lives inside it, so nothing is unpacked here.
 */
const interpretResponse = async (response, config) => {
  const body = response?.data;
  const headers = response?.headers || {};
  const contentType = headers["content-type"] || "";
  const headerFilename = filenameFromDisposition(headers["content-disposition"]);

  // Defensive: a caller not using responseType "blob" still gets a usable result.
  if (!(typeof Blob !== "undefined" && body instanceof Blob)) {
    return toResult(null, pickMessage(body) || BULK_UPLOAD_MESSAGES.result.completed);
  }

  if (detectResponseKind(contentType, headerFilename) === RESPONSE_KIND.FILE) {
    return toResult({
      blob: body,
      filename:
        headerFilename ||
        buildResultFilename(config.resultFilePrefix, contentType, headerFilename),
    });
  }

  const parsed = parseTextBody(await readBlobAsText(body));
  const message =
    typeof parsed === "string" ? parsed : pickMessage(parsed);

  return toResult(null, message || BULK_UPLOAD_MESSAGES.result.completed);
};

/**
 * Reads an error body - which arrives as a Blob under responseType "blob" -
 * and returns the clearest message available.
 */
const describeError = async (error) => {
  const data = error?.response?.data;

  if (typeof Blob !== "undefined" && data instanceof Blob) {
    const parsed = parseTextBody(await readBlobAsText(data).catch(() => ""));
    const message = typeof parsed === "string" ? parsed : pickMessage(parsed);
    if (message) return message;
  } else {
    const message = typeof data === "string" ? data : pickMessage(data);
    if (message) return message;
  }

  return error?.message || BULK_UPLOAD_MESSAGES.errors.uploadFailed;
};

/**
 * Uploads one spreadsheet for the given bulk upload type.
 *
 * @param   {string} typeId  An id from BULK_UPLOAD_TYPE.
 * @param   {File}   file    The spreadsheet to send.
 * @returns {Promise<BulkUploadResult>}
 * @throws  {Error} When the request failed.
 */
export const uploadBulkFile = async (typeId, file) => {
  const config = getBulkUploadConfig(typeId);

  if (!config?.endpoint) {
    throw new Error(BULK_UPLOAD_MESSAGES.errors.missingEndpoint(typeId));
  }

  const formData = new FormData();
  formData.append(FORM_FIELD_NAME, file);

  try {
    const response = await api.post(config.endpoint, formData, {
      headers: MULTIPART_HEADERS,
      responseType: "blob",
    });

    return await interpretResponse(response, config);
  } catch (error) {
    throw new Error(await describeError(error));
  }
};

/**
 * Sample spreadsheet URLs (S3), keyed by each config entry's templateKey.
 *
 * @returns {Promise<Object<string, string>>} Empty when the call fails.
 */
export const getBulkUploadTemplates = async () => {
  try {
    const response = await api.get("/agsp/getBulkUploadTemplates");
    return response?.data || {};
  } catch (error) {
    return {};
  }
};
